import type { RecommendationData } from '../../../shared/services/aiService';
import type { StreamEvent, StreamEventType } from '../types/chat';
import type { Restaurant } from '../../search/types/restaurant';

const isRecord = (value: unknown): value is Record<string, any> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const normalizeMessageId = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

const normalizeSequence = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

const normalizeTimestamp = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

export const adaptBffEvent = (data: unknown): StreamEvent | null => {
  if (!isRecord(data) || typeof data.type !== 'string') {
    return null;
  }

  const base = {
    type: data.type as StreamEventType,
    sequence: normalizeSequence(data.sequence),
    timestamp: normalizeTimestamp(data.timestamp),
    messageId: normalizeMessageId(data.message_id),
  };

  const payload = isRecord(data.payload) ? data.payload : {};

  switch (data.type) {
    case 'chunk':
      return {
        ...base,
        type: 'chunk',
        payload: { text: typeof payload.text === 'string' ? payload.text : '' },
      };
    case 'done':
      return {
        ...base,
        type: 'done',
        payload: {},
      };
    case 'error':
      return {
        ...base,
        type: 'error',
        payload: {
          message:
            typeof payload.message === 'string'
              ? payload.message
              : typeof data.message === 'string'
                ? data.message
                : 'Stream error',
        },
      };
    case 'recommendation':
      return {
        ...base,
        type: 'recommendation',
        payload: payload as RecommendationData,
      };
    case 'tool_result':
      return {
        ...base,
        type: 'tool_result',
        payload: {
          restaurants: Array.isArray(payload.restaurants) ? (payload.restaurants as Restaurant[]) : [],
          restaurantReasons: Array.isArray(payload.restaurantReasons) ? payload.restaurantReasons : undefined,
          updated: typeof payload.updated === 'boolean' ? payload.updated : undefined,
        },
      };
    // 1.6.0: 处理 progress 事件 — 后端 Agent 各节点完成时发送进度提示
    case 'progress':
      return {
        ...base,
        type: 'progress',
        payload: {
          step: typeof payload.step === 'string' ? payload.step : '',
          message: typeof payload.message === 'string' ? payload.message : '',
        },
      };
    default:
      return null;
  }
};

export const adaptLegacyEvent = (data: unknown): StreamEvent | null => {
  if (!isRecord(data)) {
    return null;
  }

  const base = {
    sequence: normalizeSequence(data.sequence),
    timestamp: normalizeTimestamp(data.timestamp),
  };

  if (data.error) {
    return {
      ...base,
      type: 'error',
      payload: {
        message: typeof data.message === 'string' ? data.message : 'Stream error',
      },
    };
  }

  if (data.done) {
    return {
      ...base,
      type: 'done',
      payload: {},
    };
  }

  if (Array.isArray(data.restaurants)) {
    return {
      ...base,
      type: 'tool_result',
      payload: {
        restaurants: data.restaurants as Restaurant[],
        restaurantReasons: Array.isArray(data.restaurantReasons) ? data.restaurantReasons : undefined,
        updated: typeof data.updated === 'boolean' ? data.updated : undefined,
      },
    };
  }

  if (data.recommendation) {
    return {
      ...base,
      type: 'recommendation',
      payload: data.recommendation as RecommendationData,
    };
  }

  if (typeof data.chunk === 'string') {
    return {
      ...base,
      type: 'chunk',
      payload: { text: data.chunk },
    };
  }

  return null;
};

export const normalizeStreamEvent = (data: unknown): StreamEvent | null =>
  adaptBffEvent(data) ?? adaptLegacyEvent(data);
